/**
 * The result sheet (decisions 0012 and 0013): when a creature ends the run it rises in the world,
 * then this sheet comes up from the bottom and leaves the world above it in view, where the
 * journey of decision 0031 plays. It names the creature, with its portrait, and shows the furthest
 * point beside the day's map, two small stats and what to do next. One column on a phone, one row
 * of columns on desktop. What friends see, the share text, went on 2026-10-27 (Daniel): it said
 * again what the stats say, and the sheet is lower without it. With the list of decision 0039 on,
 * today's expedition leads with its place among today's explorers instead of the creature, which
 * still rises in the world (decision 0040).
 */
import type { StringKey } from "../i18n.ts";
import { esc, icon, SQUARE } from "./dom.ts";

type T = (key: StringKey, params?: Record<string, string | number>) => string;

export interface ResultContent {
  readonly kicker: string;
  readonly heading: string;
  readonly line: string;
  /** The portrait's URL and its description, or null to leave it out. */
  readonly portrait: { readonly src: string; readonly alt: string } | null;
  readonly furthest: string;
  readonly stats: readonly [{ label: string; value: string }, { label: string; value: string }];
  /** The core's share text, its lines as they are. */
  readonly shareText: string;
  readonly kind: "daily" | "endless";
  readonly note: string;
  /** After a run played together: each member and what they found (decision 0043). */
  readonly members?: readonly { readonly name: string; readonly line: string }[];
}

/**
 * The head of today's expedition's sheet with the list of decision 0039 on, in the creature's place
 * (decision 0040): the place among today's explorers as the heading, the link to the list, the
 * name the expedition is on the list as, and the button that adds it while it is not.
 */
export interface ResultListRow {
  /** `#41 of 359 so far`, `Waiting to be added`, or the list's name while the place is unknown. */
  readonly heading: string;
  /** The link to the list: its name, or its line while the heading is the name. */
  readonly link: string;
  /** Add your name shows, the sheet's first action. */
  readonly add: boolean;
  /** `as Daniel` once the expedition is on the list, else empty. */
  readonly as: string;
}

export function resultHtml(t: T): string {
  return `
  <section class="result" role="dialog" aria-modal="false" aria-labelledby="resultTitle">
    <div class="result-grid">
      <div class="r-creature">
        <div class="r-head" data-ref="resultHead">
          <img class="portrait" alt="" data-ref="portrait">
          <div class="r-text">
            <div class="kicker" data-bind="resultKicker"></div>
            <div class="headline">${SQUARE}<h2 id="resultTitle" data-bind="resultHeading"></h2></div>
            <p class="r-line" data-bind="resultLine"></p>
          </div>
        </div>
        <div class="r-top" data-ref="listTop" hidden>
          <div class="kicker" data-bind="resultKicker"></div>
          <div class="headline">${SQUARE}<h2 id="resultPlace" data-bind="listPlace"></h2></div>
          <div class="r-top-line">
            <button type="button" class="r-list-link kicker" data-action="listOpen"><span data-bind="listLink"></span>${icon("chevronRight", 16)}</button>
            <span class="kicker r-place-as" data-bind="listAs"></span>
          </div>
          <button type="button" class="primary" data-action="listAdd" data-ref="listAdd">${esc(t("list.addTitle"))}</button>
        </div>
        <div class="stats">
          <div class="stat-big">
            <div class="kicker">${esc(t("over.furthestLabel"))}</div>
            <div class="figure" data-bind="resultFurthest"></div>
            <div class="r-map" data-ref="resultMap"></div>
          </div>
          <div class="stat-col">
            <div class="stat"><span class="kicker" data-bind="stat1Label"></span><span class="stat-value" data-bind="stat1"></span></div>
            <div class="stat"><span class="kicker" data-bind="stat2Label"></span><span class="stat-value" data-bind="stat2"></span></div>
          </div>
        </div>
        <ul class="r-members" data-ref="members" hidden></ul>
      </div>
      <div class="r-actions">
        <button type="button" class="primary" data-ref="primary"></button>
        <div class="pair" data-ref="pair">
          <button type="button" class="secondary" data-ref="second"></button>
          <button type="button" class="secondary" data-ref="third"></button>
        </div>
        <div class="kicker r-note" data-bind="resultNote"></div>
      </div>
    </div>
  </section>`;
}

/** Splits the core's share text into the map rows and the words around them. */
export function splitShare(text: string): { map: string; words: string } {
  const lines = text.split("\n");
  const isMap = (l: string) => /^[⬛⬜\u{1F3E0}\u{1F7E5}]+$/u.test(l);
  return {
    map: lines.filter(isMap).join("\n"),
    words: lines.filter((l) => !isMap(l)).join("\n"),
  };
}
